import React from 'react';
import { 
  Users, 
  Calendar, 
  Video, 
  FileText, 
  TrendingUp, 
  Activity,
  Clock,
  CheckCircle
} from 'lucide-react';
import { authService } from '../modules/auth/services/authService';

const DashboardPage = () => {
  const currentUser = authService.getCurrentUser();

  const stats = [
    {
      name: 'Total Patients',
      value: '1,234',
      change: '+12%',
      changeType: 'positive',
      icon: Users,
    },
    {
      name: 'Today\'s Appointments',
      value: '23',
      change: '+5%',
      changeType: 'positive',
      icon: Calendar,
    },
    {
      name: 'Active Sessions',
      value: '8',
      change: '-2%',
      changeType: 'negative',
      icon: Video,
    },
    {
      name: 'Documents',
      value: '456',
      change: '+8%',
      changeType: 'positive',
      icon: FileText,
    },
  ];

  const recentActivities = [
    {
      id: 1,
      type: 'appointment',
      title: 'New appointment scheduled',
      description: 'Dr. Smith scheduled with John Doe',
      time: '2 minutes ago',
      icon: Calendar,
    },
    {
      id: 2,
      type: 'session',
      title: 'Video session completed',
      description: 'Session with Sarah Johnson ended',
      time: '15 minutes ago',
      icon: Video,
    },
    {
      id: 3,
      type: 'document',
      title: 'Document uploaded',
      description: 'Medical report uploaded for Patient #123',
      time: '1 hour ago',
      icon: FileText,
    },
    {
      id: 4,
      type: 'patient',
      title: 'New patient registered',
      description: 'Mike Wilson registered in the system',
      time: '2 hours ago',
      icon: Users,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {currentUser?.first_name || 'User'}!
            </h1>
            <p className="text-gray-600 mt-1">
              Here's what's happening with your telehealth platform today.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-500">System Online</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg ${
                stat.changeType === 'positive' ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <stat.icon className={`h-6 w-6 ${
                  stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                }`} />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              <span className={`text-sm font-medium ${
                stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
              }`}>
                {stat.change}
              </span>
              <span className="text-sm text-gray-500 ml-1">from last month</span>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            <button className="text-sm text-primary-600 hover:text-primary-500 font-medium">
              View all
            </button>
          </div>
          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                    <activity.icon className="h-4 w-4 text-primary-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                  <p className="text-sm text-gray-500">{activity.description}</p>
                  <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h2>
          <div className="space-y-3">
            <button className="w-full flex items-center justify-between p-3 text-left bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-primary-600" />
                <span className="text-sm font-medium text-gray-900">Schedule Appointment</span>
              </div>
              <TrendingUp className="h-4 w-4 text-gray-400" />
            </button>
            
            <button className="w-full flex items-center justify-between p-3 text-left bg-secondary-50 hover:bg-secondary-100 rounded-lg transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <Video className="h-5 w-5 text-secondary-600" />
                <span className="text-sm font-medium text-gray-900">Start Session</span>
              </div>
              <Activity className="h-4 w-4 text-gray-400" />
            </button>
            
            <button className="w-full flex items-center justify-between p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-900">Add Patient</span>
              </div>
              <CheckCircle className="h-4 w-4 text-gray-400" />
            </button>
            
            <button className="w-full flex items-center justify-between p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-900">Upload Document</span>
              </div>
              <Clock className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            <span className="text-sm text-gray-700">Database Connection</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            <span className="text-sm text-gray-700">Salesforce Integration</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            <span className="text-sm text-gray-700">Video Service</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage; 